// ============================================
// movingAverage.js — SMA / EMA over candle closes, incremental.
//
// Price space = the candles' own (close_mcap, falling back to close), so the
// line lands on the same Y projection the candle bodies use.
//
// The cache holds the CLOSED bars (0 .. n-2). New closes extend it in O(1)
// each; the last (forming) bar is recomputed per call from the closed state,
// also O(1). Anything that changes what the closed bars mean — a new candles
// array (token / timeframe switch), a structural version bump (rebin,
// history prepend), a different first bar, type or length — rebuilds it,
// which is one O(n) pass.
//
// EMA is seeded with the SMA of the first N closes (value at index N-1),
// then ema_i = ema_{i-1} + k * (close_i - ema_{i-1}), k = 2 / (N + 1).
// ============================================

export const MA_TYPES = [
  { value: 'sma', label: 'SMA' },
  { value: 'ema', label: 'EMA' },
]
export const MA_LEN_MIN = 1
export const MA_LEN_MAX = 500

// One entry today; the store is an array so more MAs can be added later
// without a migration.
export const MA_DEFAULT = { id: 'ma1', on: false, type: 'sma', length: 20, color: '#29b6f6', weight: 2 }
export const MA_DEFAULTS = [MA_DEFAULT]

// Normalise whatever came out of storage (missing keys, bad numbers, a
// legacy single object) into a valid config array.
export function normalizeMaConfigs(raw) {
  const arr = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : [])
  const out = (arr.length ? arr : MA_DEFAULTS).map((c, i) => {
    const d = i === 0 ? MA_DEFAULT : { ...MA_DEFAULT, id: `ma${i + 1}` }
    const len = Math.round(Number(c?.length))
    const w = Math.round(Number(c?.weight))
    return {
      ...d,
      ...(c || {}),
      id: c?.id || d.id,
      on: !!c?.on,
      type: c?.type === 'ema' ? 'ema' : 'sma',
      length: Number.isFinite(len) ? Math.max(MA_LEN_MIN, Math.min(MA_LEN_MAX, len)) : d.length,
      color: typeof c?.color === 'string' && c.color ? c.color : d.color,
      weight: Number.isFinite(w) ? Math.max(1, Math.min(4, w)) : d.weight,
    }
  })
  return out
}

const closeOf = (c) => +(c?.close_mcap ?? c?.close) || 0

// Returns the cache (new or updated). vals[i] is the MA at closed bar i
// (NaN before it exists); liveVal is the MA at the last bar.
export function updateMaCache(cache, candles, cfg, structVer) {
  const n = candles ? candles.length : 0
  const len = cfg.length
  const ema = cfg.type === 'ema'
  const firstT = n > 0 ? candles[0]?.startTime : null
  const liveIdx = n - 1
  // Not keyed on array identity: the closed history is checked instead
  // (last folded close must still be what that bar says), so a fresh array
  // per tick stays incremental and a swapped history still rebuilds.
  const valid = cache
    && cache.structVer === structVer
    && cache.type === cfg.type
    && cache.length === len
    && cache.firstT === firstT
    && cache.last <= liveIdx - 1            // never shrank past what we cached
    && (cache.last < 0 || !(closeOf(candles[cache.last]) > 0) || closeOf(candles[cache.last]) === cache.closes[cache.last])
  if (!valid) {
    cache = {
      structVer, type: cfg.type, length: len, firstT,
      vals: new Float64Array(Math.max(64, n * 2)).fill(NaN),
      closes: new Float64Array(Math.max(64, n * 2)),
      last: -1,          // last closed index folded in
      sum: 0,            // SMA: sum of the last `len` closes; EMA: seed sum
      lastClose: 0,      // carry-forward for empty bars
      liveVal: NaN,
    }
  }
  if (cache.vals.length < n) {
    const cap = n * 2
    const v = new Float64Array(cap).fill(NaN); v.set(cache.vals); cache.vals = v
    const c = new Float64Array(cap); c.set(cache.closes); cache.closes = c
  }
  const k = 2 / (len + 1)
  const { vals, closes } = cache
  // Extend through newly closed bars.
  for (let i = cache.last + 1; i <= liveIdx - 1; i++) {
    let px = closeOf(candles[i])
    if (!(px > 0)) px = cache.lastClose
    else cache.lastClose = px
    closes[i] = px
    if (ema) {
      if (i < len) cache.sum += px
      if (i === len - 1) vals[i] = cache.sum / len
      else if (i >= len) vals[i] = vals[i - 1] + k * (px - vals[i - 1])
    } else {
      cache.sum += px
      if (i >= len) cache.sum -= closes[i - len]
      if (i >= len - 1) vals[i] = cache.sum / len
    }
    cache.last = i
  }
  // Forming bar — from the closed state, never folded in.
  cache.liveVal = NaN
  if (liveIdx >= 0) {
    let px = closeOf(candles[liveIdx])
    if (!(px > 0)) px = cache.lastClose
    if (ema) {
      if (liveIdx === len - 1) cache.liveVal = (cache.sum + px) / len
      else if (liveIdx >= len && Number.isFinite(vals[liveIdx - 1])) cache.liveVal = vals[liveIdx - 1] + k * (px - vals[liveIdx - 1])
    } else if (liveIdx >= len - 1) {
      const out = liveIdx >= len ? closes[liveIdx - len] : 0
      cache.liveVal = (cache.sum + px - out) / len
    }
  }
  return cache
}

export const maValueAt = (cache, i, liveIdx) => (i === liveIdx ? cache.liveVal : cache.vals[i])
